import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminInjuries() {
    const [intensity, setIntensity] = useState(1.0);
    const [injuries, setInjuries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState("active");

    const load = async () => {
        try {
            const [s, l] = await Promise.all([
                api.get("/injuries/settings"),
                api.get("/injuries", { params: { status: statusFilter } }),
            ]);
            setIntensity(s.data.injury_intensity ?? 1.0);
            setInjuries(l.data.injuries || []);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [statusFilter]);

    const saveIntensity = async (val) => {
        setSaving(true);
        try {
            const v = Math.max(0, Math.min(3, parseFloat(val)));
            await api.put("/injuries/settings", { intensity: v });
            setIntensity(v);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const clearInjury = async (id) => {
        if (!confirm("Far rientrare questo giocatore subito?")) return;
        try {
            await api.delete(`/injuries/${id}`);
            await load();
        } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const activeCount = injuries.filter((i) => i.status === "active").length;
    const intensityLabel = intensity <= 0.4 ? "Bassissima"
        : intensity <= 0.8 ? "Bassa"
        : intensity <= 1.2 ? "Standard"
        : intensity <= 1.8 ? "Alta"
        : "Molto Alta";

    return (
        <div data-testid="admin-injuries">
            <div className="op-label text-tff7a1a">// SISTEMA INFORTUNI</div>
            <h2 className="op-heading text-3xl mt-2">Gestione Infortuni</h2>
            <p className="text-t8f95a3 text-sm mt-2 max-w-2xl">
                Gli infortuni si generano <span className="text-tffffff">automaticamente</span> ad ogni tabellino salvato, pesati dalla resistenza PES di ogni giocatore (Low/Medium/High). Modifica l&apos;intensità globale se ne escono troppi o troppo pochi.
            </p>

            {/* Intensity slider */}
            <div className="op-card mt-6 p-5" style={{ borderTop: "3px solid rgb(var(--tc-ff7a1a))" }}>
                <div className="op-label text-[10px] text-tff7a1a mb-2">INTENSITÀ GLOBALE</div>
                <div className="flex items-center gap-4">
                    <input
                        type="range" min="0" max="3" step="0.1"
                        value={intensity}
                        onChange={(e) => setIntensity(parseFloat(e.target.value))}
                        onMouseUp={(e) => saveIntensity(e.target.value)}
                        onTouchEnd={(e) => saveIntensity(e.target.value)}
                        disabled={saving}
                        className="flex-1 accent-tff7a1a"
                        data-testid="intensity-slider"
                    />
                    <div className="op-mono text-2xl text-tffffff font-bold w-16 text-center">
                        {intensity.toFixed(1)}x
                    </div>
                    <span className="op-label text-[10px] text-tff7a1a min-w-[110px] text-right">
                        {intensityLabel}
                    </span>
                </div>
                <div className="op-mono text-[10px] text-t5c6370 mt-2">
                    0.0x = nessun infortunio · 1.0x = standard (~2% per giocatore Medium) · 3.0x = stagione brutale
                </div>
            </div>

            {/* Filter + list */}
            <div className="flex items-center gap-2 mt-8 mb-3">
                <button
                    onClick={() => setStatusFilter("active")}
                    className={`op-label text-[10px] px-3 py-1.5 border ${statusFilter === "active" ? "border-tff7a1a text-tffffff bg-tff7a1a/15" : "border-t22252a text-tc9ccd3"}`}
                    data-testid="filter-active"
                >
                    Attivi
                </button>
                <button
                    onClick={() => setStatusFilter("cleared")}
                    className={`op-label text-[10px] px-3 py-1.5 border ${statusFilter === "cleared" ? "border-t7ce38b text-tffffff bg-t7ce38b/15" : "border-t22252a text-tc9ccd3"}`}
                    data-testid="filter-cleared"
                >
                    Rientrati
                </button>
                <button
                    onClick={() => setStatusFilter("all")}
                    className={`op-label text-[10px] px-3 py-1.5 border ${statusFilter === "all" ? "border-t2bb6ff text-tffffff bg-t2bb6ff/15" : "border-t22252a text-tc9ccd3"}`}
                    data-testid="filter-all"
                >
                    Tutti
                </button>
                <span className="op-mono text-[11px] text-t5c6370 ml-auto">
                    {injuries.length} record · {activeCount} attivi
                </span>
            </div>

            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
            ) : injuries.length === 0 ? (
                <div className="op-card p-8 text-center text-t8f95a3 text-sm">
                    Nessun infortunio {statusFilter === "active" ? "attivo" : statusFilter === "cleared" ? "concluso" : ""}.
                </div>
            ) : (
                <div className="op-card overflow-hidden">
                    <table className="min-w-full text-sm">
                        <thead className="bg-t0e1014 border-b border-t22252a">
                            <tr>
                                <th className="px-4 py-3 text-left op-label text-[10px] text-tff7a1a">Giocatore</th>
                                <th className="px-4 py-3 text-left op-label text-[10px] text-tff7a1a">Squadra</th>
                                <th className="px-4 py-3 text-center op-label text-[10px] text-tff7a1a">Durata (partite)</th>
                                <th className="px-4 py-3 text-center op-label text-[10px] text-tff7a1a">Giornate</th>
                                <th className="px-4 py-3 text-left op-label text-[10px] text-tff7a1a">Origine</th>
                                <th className="px-4 py-3 text-center op-label text-[10px] text-tff7a1a">Stato</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {injuries.map((i, idx) => (
                                <tr
                                    key={i.id}
                                    className={`border-b border-t16181c ${idx % 2 === 0 ? "bg-t0a0b0e" : "bg-t0d0e12"}`}
                                    data-testid={`injury-row-${i.id}`}
                                >
                                    <td className="px-4 py-2 text-tffffff">
                                        {i.player_name}
                                        <div className="op-mono text-[10px] text-t5c6370">
                                            {i.position} · res {i.resistance}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 text-tc9ccd3">
                                        {i.team?.logo_url && (
                                            <img src={i.team.logo_url} alt="" className="w-5 h-5 object-contain inline mr-1.5" />
                                        )}
                                        {i.team?.name || "—"}
                                    </td>
                                    <td className="px-4 py-2 text-center op-mono text-tffffff">{i.weeks_total} <span className="text-[10px] text-t5c6370">partite</span></td>
                                    <td className="px-4 py-2 text-center op-mono">
                                        {i.start_giornata && i.end_giornata ? (
                                            <span className="text-tff7a1a font-bold">
                                                {i.start_giornata === i.end_giornata
                                                    ? `G${i.start_giornata}`
                                                    : `G${i.start_giornata}–G${i.end_giornata}`}
                                            </span>
                                        ) : (
                                            <span className="text-t5c6370">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-t8f95a3 text-xs op-mono">{i.starts_at_label || "—"}</td>
                                    <td className="px-4 py-2 text-center">
                                        {i.status === "active" ? (
                                            <span className="op-label text-[9px] px-2 py-0.5 border border-tff7a1a text-tff7a1a">ATTIVO</span>
                                        ) : (
                                            <span className="op-label text-[9px] px-2 py-0.5 border border-t7ce38b text-t7ce38b">RIENTRATO</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        {i.status === "active" && (
                                            <button
                                                onClick={() => clearInjury(i.id)}
                                                className="op-btn op-btn-ghost text-[10px]"
                                                data-testid={`clear-injury-${i.id}`}
                                            >
                                                Far rientrare
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {error && <div className="text-tff4d4d text-sm mt-4">{error}</div>}
        </div>
    );
}
